// The guided tour of the ECR Tracker, for the delivery team and the PMs who
// live on this board: where an ECR is in the Rolls-Royce lifecycle, whose it
// is, how long it has sat there, what is stopping it, and how one comes off
// the board without being lost.
//
// Steps point at [data-tour] anchors on EcrTrackerPage. The last two describe
// the Archived view, which is a different view of the page, so every step
// carries a `before` that puts the page on the view it describes; stepping
// backwards then leaves the page as it was going forwards. The engine
// (shared/FeatureTour.jsx) and the show-once record (hooks/useFeatureTour.js)
// are generic; only this file knows about ECRs.

export const ECR_TRACKER_TOUR_ID = 'ecr-tracker';

// The Active / Archived switch is the only view change the tour makes. Both
// buttons live inside the same anchor, so the hooks find them by their label
// rather than by an anchor of their own.
const switchTo = (label) => {
  const toggle = document.querySelector('[data-tour="archive-toggle"]');
  const btn = [...(toggle?.querySelectorAll('button') || [])].find(b => b.textContent.trim() === label);
  btn?.click();
};
const onActive = () => switchTo('Active ECRs');
const onArchived = () => switchTo('Archived');

export const ecrTrackerTourSteps = [
  {
    title: 'The ECR Tracker',
    target: '[data-tour="page-header"]',
    before: onActive,
    body: [
      'Every Rolls-Royce ECR we are running is a card here, and the board answers five questions at a glance: where it is, whose it is, how long it has been there, what is stopping it and what happens next. Nothing on this page stores ECR paperwork; a card carries a link to wherever that lives.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. Either way it will not pop up again; the ? by the title replays it.',
    ],
  },
  {
    title: 'The lifecycle, left to right',
    target: '[data-tour="board"]',
    before: onActive,
    body: [
      'Twelve lanes in three groups. Pre-work runs from ECR1/2 Awaiting Assignment, where the PM\'s request lands, through ECR1/2 In Progress while a designer prepares it, to ECR1/2 Submitted - Awaiting ECR3. Delivery is ECR3 Received - Work Active and then Work Complete - ECR4 Required. Post-work takes ECR4 through the same steps to ECR5 Received, the end of the lifecycle.',
      'Drag a card into its new lane, or use Move to in the card panel. Either way the stage clock restarts and the move is recorded. No lane is locked: the board follows the work, so move a card when the real activity moves.',
      'The board fits your window, and a long lane scrolls its own cards. To move sideways, press and drag any empty space on the board, or use the scrollbar along its bottom edge.',
      'Two lanes are the milestones the board exists to make visible: nothing may start before ECR3 is received, and nothing may be billed before ECR5 Received. Lanes waiting on Rolls-Royce count Waiting rather than In stage, so a card sitting with the client does not read as one we are sitting on.',
    ],
  },
  {
    title: 'Where everything stands',
    target: '[data-tour="tiles"]',
    before: onActive,
    body: [
      'The tiles count the active board: Awaiting ECR3 (cannot start work), Approved to Work, ECR4 Required, Awaiting ECR5 (cannot yet bill), Billable, Blocked, Overdue and RR SLA Breached. Click one to filter the board down to it, and click it again to come back.',
      'Blocked is a flag rather than a lane, because the useful thing to know is where in the lifecycle a card is stuck, not that it is stuck somewhere. Overdue means the ECR1/2 Submission Due Date has passed and the ECR1/2 has not been submitted yet; once it is submitted the date stops counting. A card can be both.',
    ],
  },
  {
    title: 'Finding one ECR',
    target: '[data-tour="search"]',
    before: onActive,
    body: [
      'Search matches the project number, the project name, the PM, the designer and the linked SOW reference. Beside it, filters for a single PM or a single designer.',
      'Searching or filtering scrolls the first lane that still has cards into view, so a hit is never off the edge of a board you had scrolled to the other end.',
    ],
  },
  {
    title: 'Quick filters, and what changed for you',
    target: '[data-tour="quick-filters"]',
    before: onActive,
    body: [
      'My Projects narrows the board to the ECRs you are PM for. The rest match the tiles, so whichever you reach for, the board and the counts agree.',
      'When somebody else changes an ECR you are the PM or designer on (moves it, blocks it, changes the next action, the dates or the designer), the card gets a gold dot with the latest change on it, and ECR Tracker in the sidebar shows how many of your ECRs have changed. Your own changes never count.',
      'Open the card to see everything that changed since you last looked, at the top of its panel. Opening it clears the dot and the count for you alone; anyone else named on the card keeps theirs until they open it. Updated for me filters the board down to those cards.',
      'Each weekday morning you also get one email listing the changes you have not opened yet, with a link straight to each card. A change is emailed once, however long it goes unopened.',
    ],
  },
  {
    title: 'Kanban or table',
    target: '[data-tour="view-toggle"]',
    before: onActive,
    body: [
      'The same ECRs as a table when you want to read down a column rather than across a board: position, age, ECR1/2 due date, flags and next action side by side.',
    ],
  },
  {
    title: 'Raising an ECR',
    target: '[data-tour="new-ecr"]',
    before: onActive,
    body: [
      'New ECR takes the SOW and lifts the project number, project name and PM from it. Where the project is not a SOW yet, tick No SOW yet and type the project number; you can link the SOW later from the card without losing anything.',
      'The card always starts in ECR1/2 Awaiting Assignment, so you do not choose a lane. The designer can be anyone on the staff list, with those already designing ECRs listed first, and is optional at this point: the ECR team usually assigns one when they pick the card up, then moves it to ECR1/2 In Progress. The ECR1/2 Submission Due Date is the date the ECR1/2 has to be submitted to Rolls-Royce by, not when the work or ECR3 is due.',
    ],
  },
  {
    title: 'One card, everything about it',
    target: '[data-tour="card-menu"]',
    before: onActive,
    body: [
      'Click a card to open its panel: the PM and designer, the ECR1/2 Submission Due Date, the next action, the blocker, a link to the ECR documents, notes, Move to, and the full history of every move and every change to the card. The ... on the card is the same thing by another route, with Edit ECR and View History, which opens the panel on the history.',
      'Everything on the panel is saved with Save Changes, except the lane, which only ever changes by moving the card. That keeps the ageing and the history honest.',
    ],
  },
  {
    title: 'Review, then submit',
    target: '[data-tour="board"]',
    before: onActive,
    body: [
      'When the designer has finished, they move the card to ECR1/2 Awaiting AV Review (or ECR4 Awaiting AV Review later on). That is our own quality check. There is nothing to tick: once the review is done, move the card on to Ready to Submit, and the history records who moved it and when.',
      'Ready to Submit is kept separate on purpose. The team often reviews several ECRs and submits them together later in the day, so a card there has passed review and is waiting to go to Rolls-Royce.',
      'For ECR4 the designer may be somebody other than whoever prepared ECR1/2. Change the designer on the card when it reaches Work Complete - ECR4 Required; every assignment is kept in the history.',
    ],
  },
  {
    title: 'Rolls-Royce has 10 working days',
    target: '[data-tour="quick-filters"]',
    before: onActive,
    body: [
      'Moving a card into ECR1/2 Submitted - Awaiting ECR3 or ECR4 Submitted - Awaiting ECR5 starts the Rolls-Royce response SLA: 10 working days, Monday to Friday, from the day it went in. The card shows it as RR SLA: 7 days remaining · Due 16 Oct, amber with two working days or fewer to go, orange on the day it is due, and red once it is breached, with the working days it is overdue.',
      'Moving the card on to ECR3 Received - Work Active or ECR5 Received records the response and stops the clock. If Rolls-Royce sends a query or asks for an amendment instead, open the card and use Record RR Response: that stops the clock too, without moving the card. When the amended ECR goes back, moving it into Submitted again (or Record resubmission on the card) starts a fresh 10 working days, and every cycle is kept.',
      'The RR SLA filters pick out the cards whose clock is running, approaching, due today or breached, and the RR SLA Breached tile counts the live breaches. An archived ECR drops out of both and keeps its SLA history.',
    ],
  },
  {
    title: 'Correcting a date',
    target: '[data-tour="card-menu"]',
    before: onActive,
    body: [
      'Moving a card records the day it moved. For an ECR entered after the event, such as the backlog, that is the wrong date: an ECR1/2 submitted to Rolls-Royce on 15 September but moved here on 5 October would otherwise read as submitted on 5 October.',
      'Lifecycle dates in the card panel lists when each step happened. Edit beside a date corrects that one; Edit lifecycle dates sets them all at once, which is quickest for a backlog ECR. A reason is always required. You do not need to drag the card back and forth to do it.',
      'The corrected date is what the RR SLA, Days in Stage and the counts work from. The history keeps the move as it was recorded and adds the correction beside it, with the previous date, the new one, who changed it, when and why. A date out of sequence, or in the future, asks you to confirm with Save Anyway rather than refusing it.',
    ],
  },
  {
    title: 'Archiving an ECR',
    target: '[data-tour="card-menu"]',
    before: onActive,
    body: [
      'Cancelled, duplicated or simply no longer wanted: Archive ECR on the ... menu takes the card off the live board. It asks you to confirm, naming the ECR, and takes an optional reason. There is no Delete, deliberately, so nothing can be lost by a slip of the mouse.',
      'It works from any lane, because it is an administrative decision rather than a step in the lifecycle. It does not move the card, so there is no Archive lane on the board; the card simply goes. It leaves every dashboard count with it.',
    ],
  },
  {
    title: 'The Archived view',
    target: '[data-tour="archived"]',
    before: onArchived,
    body: [
      'Archived is a searchable table rather than a second board: the project number and name, the PM, the lane it was in when it was archived, when, who did it and why. Search it by project number or project name.',
      'Restore puts an ECR back on the board in that same lane, with its stage clock restarted from today, so the months it spent archived are not read as months in stage. The archive and the restore are both added to the ECR history, reason and all, and nothing recorded earlier is overwritten.',
    ],
  },
  {
    title: 'Back to the board',
    target: '[data-tour="archive-toggle"]',
    before: onActive,
    body: [
      'Active ECRs is where the page always opens, and it is the one to work from: it is the live operational view, and an archived ECR never appears in it or in any of its counts until somebody restores it.',
    ],
  },
];
